import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Observable, ReplaySubject, debounceTime, of, take } from 'rxjs';

import { KeyValueElevatedRoleModel, KeyValueModel } from '@models/common';
import { SearchViewModel } from '@models/module/grid';
import { TenantSettingsEnum } from '@models/tenant';
import { AssignUsersDialogComponent } from '@module-instance/assignments/assign-users-dialog/assign-users-dialog.component';
import { SelectUsersParameters } from '@module-instance/assignments/assign-users-dialog/assign-users-dialog.model';
import { AssignmentsSkeletonComponent } from '@module-instance/assignments/assignments-skeleton/assignments-skeleton.component';
import {
  Assignments,
  AssignmentsPipe,
  AssignmentsService,
  ModuleIds,
  SyncRolePositionModel,
  SyncUsersAndRolesModel,
} from '@module-instance/assignments/common';
import { PrimaryUsersDialogComponent } from '@module-instance/assignments/primary-users-dialog/primary-users-dialog.component';
import {
  RoleOrderingDialogComponent,
  RoleOrderingDialogData,
} from '@module-instance/assignments/role-ordering-dialog/role-ordering-dialog.component';
import { CipoSelectComponent } from '@shared/components/fields/cipo-select/cipo-select.component';
import { CipoFieldTypes, CipoListControl } from '@shared/components/fields/common';
import { VirtualListModule } from '@shared/components/virtual-list';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { CipoFormModule } from '@shared/modules';
import { NotificationService, UserService, UtilsService } from '@shared/services';
import { DictionaryStore } from '@signal-store';
import { TenantStore } from '@signal-store/tenant.store';

@Component({
  selector: 'cipo-assignments',
  imports: [
    TranslateModule,
    CipoFormModule,
    CipoSelectComponent,
    VirtualListModule,
    MatMenuModule,
    AssignmentsPipe,
    AssignmentsSkeletonComponent,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './assignments.component.html',
  styleUrls: ['./assignments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentsComponent implements OnInit {
  translate = inject(TranslateService);
  assignmentsService = inject(AssignmentsService);
  notification = inject(NotificationService);
  utilsService = inject(UtilsService);
  userService = inject(UserService);
  dictionaryStore = inject(DictionaryStore);

  tenantStore = inject(TenantStore);
  private dialog = inject(MatDialog);

  @Output() rolesChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() assignmentsChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() entityInstanceId: number;
  @Input() moduleId: number;
  @Input() hasUpdate: boolean;

  readonly showActions = input(true);

  MODULE_IDS = ModuleIds;
  assignments = signal<Assignments[]>([]);
  initialAssignments: Assignments[] = [];
  initialSearchAssignments = computed(() => {
    const searchValue = this.searchValue();
    return this.assignments()?.filter(
      row =>
        row?.userName?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
        row?.userOrgName?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
        row?.userEmail?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
        row?.roles?.some(r => r.value?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase())),
    );
  });
  anyAssignmentsChanged = computed(() => {
    return this.assignments()?.some(a => a?.deleting || a?.edited || a?.isNew);
  });
  totalAllocationHours = computed(() => {
    return this.assignments()
      ?.map(d => d?.allocationHoursPerMonth ?? 0)
      ?.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
  });

  readonly totalSkeleton = { width: '260px', height: '14px', margin: '0' };
  searchModel: SearchViewModel = {};
  searchValue = signal('');
  searchControl = new FormControl('');
  propagateHierarchy = signal(false);
  loading = signal(true);
  saving = signal(false);
  discardRequested = signal(false);
  ordering = signal(false);
  userRateEnabled = signal(false);
  userAllocationEnabled = signal(false);
  roles: KeyValueElevatedRoleModel<number, string>[] = [];
  arrayControls: Record<number, CipoListControl> = {};
  disabledForm = computed(() => this.ordering() || this.saving() || this.locked());

  private readonly locked = signal(false);
  @Input()
  set entityInstanceLocked(value: boolean) {
    this.locked.set(value);

    if (Object.keys(this.arrayControls).length) {
      this.disableEnableControls(value);
    }
  }

  constructor() {
    effect(() => {
      const disabled = this.ordering() || this.saving();

      // Check to avoid multiplication
      if (Object.values(this.arrayControls)[0]?.disabled === disabled) {
        return;
      }

      this.disableEnableControls(disabled);
    });
  }

  ngOnInit(): void {
    const tenantSettings = this.tenantStore.getSettings([
      TenantSettingsEnum.user_allocation,
      TenantSettingsEnum.user_rate,
    ]);
    this.userRateEnabled.set(
      this.utilsService.convertValueToBoolean(
        tenantSettings.find(tenantSetting => tenantSetting.id == TenantSettingsEnum.user_rate).value,
      ),
    );
    this.userAllocationEnabled.set(
      this.utilsService.convertValueToBoolean(
        tenantSettings.find(tenantSetting => tenantSetting.id == TenantSettingsEnum.user_allocation).value,
      ),
    );

    this.searchControl.valueChanges
      .pipe(debounceTime(DEBOUNCE_TIMES.default))
      .subscribe(value => this.searchValue.set(value));
    this.dictionaryStore.getRoles().subscribe({
      next: roles => {
        this.roles = roles;
        this.loadData();
      },
      error: () => this.loading.set(false),
    });
  }

  disableEnableControls(disabled: boolean) {
    Object.values(this.arrayControls).forEach(control => {
      if (disabled) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  roleNames(userId: number): string[] {
    const roleIds = this.arrayControls[userId]?.value ?? [];
    return roleIds.map(id => this.roles.find(role => role.key === id)?.value).filter(value => value != null);
  }

  loadData() {
    // clear criteria and assignments
    this.searchModel.criteria = '';
    this.assignments.set([]);
    this.loading.set(true);

    this.assignmentsService.getAssignments(this.searchModel, this.entityInstanceId).subscribe({
      next: a => {
        this.assignments.set(a.data);
        this.initialAssignments = structuredClone(this.assignments());
        this.generateControls();
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  generateControls() {
    this.arrayControls = {};

    for (let index = 0; index < this.assignments().length; index++) {
      const assignment = this.assignments()[index];
      assignment.roles = assignment.roles || [];
      assignment.roleIds = assignment.roleIds || [];

      const orderedRoles = this.getOrderedRoles(assignment, assignment.roleIds);
      assignment.roleIds = orderedRoles.map(role => role.key);

      // Material sorts multiple selections by option order. Keep assigned roles first in BE order.
      const optionRoles = [
        ...orderedRoles.map(role => this.roles.find(option => option.key === role.key) ?? role),
        ...this.roles.filter(role => !assignment.roleIds.includes(role.key)),
      ];
      const assignmentRoles = optionRoles.map(role => ({
        key: role.key,
        value: role.value,
        disabled: this.roles.find(option => option.key === role.key)?.invalid,
      }));

      assignmentRoles.forEach(role => {
        const isElevated = this.roles.some(r => r.key === role.key && r.isElevated);
        const isGlobal = assignment.globalRoles.some(r => r.key === role.key);
        const isAssigned = assignment.roles.some(r => r.key === role.key);

        // only global elevated roles can be assigned to the user
        if (isElevated && !isGlobal && !role.disabled) {
          role.disabled = true;
        }

        // if the role is already assigned, it should not be disabled
        if (isAssigned) {
          role.disabled = false;
        }
      });

      const control = new CipoListControl(assignment.roleIds, {
        label: 'assignments.roles',
        multiple: true,
        type: CipoFieldTypes.Select,
        options: assignmentRoles,
      });

      control.valueChanges.pipe(take(1)).subscribe(() => {
        this.assignments.update(assignments => {
          assignments[index].edited = true;
          return [...assignments];
        });
        this.rolesChanged.emit(true);
      });

      this.arrayControls[assignment.userId] = control;
    }

    if (this.locked()) {
      this.disableEnableControls(true);
    }
  }

  deleteAssignment(userId: number) {
    this.rolesChanged.emit(true);

    this.assignments.update(assignments => {
      assignments.find(a => a.userId === userId)!.deleting = true;
      return [...assignments];
    });
    this.arrayControls[userId].setValue([]);
    this.arrayControls[userId].disable();
  }

  restoreDeleteAssignment(userId: number) {
    const assignment = this.assignments().find(item => item.userId === userId);
    if (!assignment) return;

    this.assignments.update(assignments => {
      assignments.find(a => a.userId === assignment.userId)!.deleting = false;
      return [...assignments];
    });
    this.arrayControls[assignment.userId].setValue(assignment.roleIds);
    this.arrayControls[assignment.userId].enable();
  }

  somethingChange(userId: number) {
    this.assignments.update(assignments => {
      assignments.find(a => a.userId === userId)!.edited = true;
      return [...assignments];
    });
    this.rolesChanged.emit(true);
  }

  private getOrderedRoles(assignment: Assignments, roleIds: number[]): KeyValueModel<number, string>[] {
    const existingRoles = assignment.roles ?? [];
    const retainedRoles = existingRoles.filter(role => roleIds.includes(role.key));
    const addedRoles = this.roles.filter(
      role => roleIds.includes(role.key) && !existingRoles.some(existing => existing.key === role.key),
    );
    // Preserve assignment-specific ranks, which are absent from the role dictionary.
    const lastOrder = Math.max(0, ...existingRoles.map(role => role.order ?? 0));
    return [
      ...retainedRoles,
      ...addedRoles.map((role, index) => ({ key: role.key, value: role.value, order: lastOrder + index + 1 })),
    ];
  }

  saveAssignments(): Observable<boolean> {
    // clear search on save
    this.clearInput();

    if (!this.assignments()?.length) {
      return of(false);
    }

    this.saving.set(true);

    for (let index = 0; index < this.assignments().length; index++) {
      const roleIds = this.arrayControls[this.assignments()[index].userId]?.value ?? [];

      const assignment = this.assignments()[index];
      assignment.roles = this.getOrderedRoles(assignment, roleIds);
      assignment.roleIds = assignment.roles.map(role => role.key);
    }

    const syncModel: SyncUsersAndRolesModel = {
      id: this.entityInstanceId,
      propagateOnHierarchy: this.propagateHierarchy(),
      users: this.assignments().filter(a => !a.deleting),
    };

    const saved = new ReplaySubject<boolean>(1);

    this.assignmentsService.syncAssignments(syncModel).subscribe({
      complete: () => {
        this.notification.success('assignments.successfully');
        this.loadData();
        this.rolesChanged.emit(false);
        this.assignmentsChanged.emit(true);
        this.saving.set(false);
        this.discardRequested.set(false);
        this.propagateHierarchy.set(false);
        saved.next(true);
        saved.complete();
      },
      error: () => {
        this.saving.set(false);
        saved.next(false);
        saved.complete();
      },
    });

    return saved.asObservable();
  }

  clearInput() {
    this.searchControl.setValue('');
  }

  openSelectUsersModel() {
    const params: SelectUsersParameters = {
      entityInstanceId: this.entityInstanceId,
      existingUsers: this.assignments()
        .filter(a => !a.deleting)
        .map(a => a.userId),
    };

    this.dialog
      .open<AssignUsersDialogComponent, SelectUsersParameters, Assignments[]>(AssignUsersDialogComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        ...this.userService.getResponsiveDialogSize().md,
        data: params,
      })
      .afterClosed()
      .subscribe(assignments => {
        if (assignments?.length) {
          // clear search on save
          this.clearInput();

          this.assignments.set([...assignments, ...this.assignments()]);
          this.generateControls();

          this.assignmentsChanged.emit(true);
        }
      });
  }

  openPrimaryUsersModel() {
    this.dialog
      .open(PrimaryUsersDialogComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        data: this.entityInstanceId,
        ...this.userService.getResponsiveDialogSize().sm,
      })
      .afterClosed()
      .subscribe((status: boolean) => {
        if (status) {
          this.loadData();
        }
      });
  }

  virtualTrackBy(_index: number, assignment: Assignments): number {
    return assignment.userId;
  }

  openRoleOrderingDialog(assignment: Assignments) {
    this.ordering.set(true);

    this.dialog
      .open<RoleOrderingDialogComponent, RoleOrderingDialogData, KeyValueModel<number, string>[]>(
        RoleOrderingDialogComponent,
        {
          panelClass: ['cipo-dialog', 'classic'],
          ...this.userService.getResponsiveDialogSize().sm,
          data: {
            userName: assignment.userName,
            roles: structuredClone(assignment.roles),
          },
        },
      )
      .afterClosed()
      .subscribe(roles => {
        this.ordering.set(false);
        if (roles) this.saveOrdering(assignment.userId, roles);
      });
  }

  private saveOrdering(userId: number, roles: KeyValueModel<number, string>[]) {
    const assignment = this.assignments().find(item => item.userId === userId);
    if (!assignment) return;

    const orderedRoles = roles.map((role, index) => ({ ...role, order: index + 1 }));

    this.assignments.update(assignments => {
      const index = assignments.findIndex(a => a.userId === userId);
      if (index !== -1) {
        assignments[index] = { ...assignments[index], roles: orderedRoles };
      }
      return [...assignments];
    });

    const model: SyncRolePositionModel = {
      entityInstanceId: this.entityInstanceId,
      userId,
      roles: orderedRoles,
    };

    this.assignmentsService.syncRolePosition(model).subscribe(() => {
      this.notification.success('assignments.rolesSuccessfully');
      this.ordering.set(false);
      this.loadData();
    });
  }

  requestDiscard(): void {
    if (this.anyAssignmentsChanged()) {
      this.discardRequested.set(true);
    }
  }

  cancelDiscard(): void {
    this.discardRequested.set(false);
  }

  discardChanges() {
    // Clear the search so restored assignments are visible.
    this.clearInput();
    this.assignments.set(structuredClone(this.initialAssignments));
    this.propagateHierarchy.set(false);
    this.discardRequested.set(false);
    this.generateControls();
    this.rolesChanged.emit(false);
  }
}
